import { Button, Card, Empty, Result, Space, Table, theme } from "antd";
import type { TableColumnsType } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { adminApi } from "@/api/client";

type Row = Record<string, unknown>;

export function AuditLogsPage() {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page") ?? "1") || 1;
  const perPage = Number(searchParams.get("per_page") ?? "20") || 20;

  const audit = useQuery({
    queryKey: ["admin", "auditLogs", searchParams.toString()],
    queryFn: async () => {
      const { data, response } = await adminApi.GET("/admin/v1/audit-logs", {
        params: {
          query: { page, per_page: perPage },
        },
      });
      if (!response.ok || !data) throw new Error(`audit:${response.status}`);
      return data;
    },
  });

  const columns: TableColumnsType<Row> = useMemo(() => {
    const first = audit.data?.items?.[0];
    const keys =
      first && typeof first === "object" && first !== null
        ? Object.keys(first as Record<string, unknown>).slice(0, 8)
        : [];
    const base = keys.length
      ? keys
      : ["action", "actor_id", "target", "reason", "created_at"];
    return base.map((k) => ({
      title: k,
      dataIndex: k,
      ellipsis: true,
      render: (v: unknown) => String(v ?? "—"),
    }));
  }, [audit.data]);

  const rows = audit.data?.items ?? [];

  return (
    <Card title={t("audit.title")}>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div style={{ color: token.colorTextSecondary }}>
          {t("audit.subtitle")}
        </div>

        {audit.error ? (
          <Result
            status="warning"
            title={t("audit.error")}
            extra={
              <Button type="primary" onClick={() => audit.refetch()}>
                {t("common.retry")}
              </Button>
            }
          />
        ) : (
          <Table<Row>
            loading={audit.isFetching}
            rowKey={(r, idx) => safeRowKey(r, idx ?? 0, page)}
            dataSource={(rows ?? []) as Row[]}
            columns={columns.length ? columns : [{ title: "—", render: () => "—" }]}
            locale={{
              emptyText: (
                <Empty description={t("audit.empty")}>
                  <Button type="link" onClick={() => audit.refetch()}>
                    {t("common.retry")}
                  </Button>
                </Empty>
              ),
            }}
            pagination={{
              current: page,
              total: audit.data?.total ?? 0,
              pageSize: perPage,
              showSizeChanger: true,
              pageSizeOptions: [20, 50, 100],
              position: ["bottomCenter"],
              onChange: (p, ps) => {
                const next = new URLSearchParams(searchParams);
                next.set("page", String(p));
                next.set("per_page", String(ps));
                setSearchParams(next, { replace: true });
              },
            }}
          />
        )}
      </Space>
    </Card>
  );
}

function safeRowKey(r: Row, idx: number, page: number): string {
  const id = r.id ?? r.request_id ?? r.created_at ?? r.sequence;
  if (id !== undefined && id !== null) return `${String(id)}`;
  return `row-${page}-${idx}`;
}
